import { useEffect, useState } from 'react';
import { Building2, CheckCircle, XCircle, Loader2, AlertTriangle, Save, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/apiHeaders';
import { getTenantUrl } from '../utils/tenantDetection';
import { toast } from 'sonner';

/**
 * Firm › Settings › Firm profile (RESTORE_PLAN R3.1, the D1 restore): a firm
 * admin renames the firm and changes its subdomain, with a live availability
 * check, and sees the stats line the old FirmSettings card showed. Backed by
 * GET/PUT /firms/:firmId and GET /firms/:firmId/subdomain-check.
 */

export interface FirmProfile {
  id: string;
  name: string;
  subdomain: string;
  customDomain: string | null;
  subscriptionTier: string | null;
  createdAt: string | null;
}

export interface FirmStats {
  clientCompanies: number;
  firmMembers: number;
  clientUsers: number;
  activeSOWs: number;
}

type Availability = { state: 'idle' | 'checking' | 'available' | 'taken' | 'unknown'; reason?: string };

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

/** The subdomain field, as the server stores it. */
export function cleanSubdomainInput(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9-]/g, '');
}

/** Whether Save may be pressed: a name, and a subdomain that is unchanged or known free. */
export function canSaveProfile(name: string, subdomain: string, original: string, availability: Availability['state']): boolean {
  if (!name.trim() || !subdomain.trim()) return false;
  if (subdomain === original) return true;
  return availability === 'available';
}

export const STAT_LABELS: { key: keyof FirmStats; label: string }[] = [
  { key: 'clientCompanies', label: 'Client companies' },
  { key: 'firmMembers', label: 'Firm members' },
  { key: 'clientUsers', label: 'Client users' },
  { key: 'activeSOWs', label: 'Active SOWs' },
];

export function FirmProfileSettings({ accessToken, firmId }: { accessToken: string; firmId: string }) {
  const [firm, setFirm] = useState<FirmProfile | null>(null);
  const [stats, setStats] = useState<FirmStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [availability, setAvailability] = useState<Availability>({ state: 'idle' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [redirectingTo, setRedirectingTo] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE}/firms/${firmId}`, { headers: getAuthHeaders(accessToken) });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Could not load the firm (${res.status})`);
      }
      const data = await res.json();
      setFirm(data.firm);
      setStats(data.stats);
      setName(data.firm?.name || '');
      setSubdomain(data.firm?.subdomain || '');
    } catch (e: any) {
      setError(e?.message || 'Could not load the firm');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [firmId, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Availability check, debounced, only while editing and only when the subdomain changed.
  useEffect(() => {
    if (!editing || !firm || !subdomain || subdomain === firm.subdomain) {
      setAvailability({ state: 'idle' });
      return;
    }
    const timer = setTimeout(async () => {
      setAvailability({ state: 'checking' });
      try {
        const res = await fetch(`${BASE}/firms/${firmId}/subdomain-check?subdomain=${encodeURIComponent(subdomain)}`, { headers: getAuthHeaders(accessToken) });
        if (!res.ok) throw new Error(`check failed (${res.status})`);
        const data = await res.json();
        setAvailability(data.available ? { state: 'available' } : { state: 'taken', reason: data.reason || 'This subdomain is already taken' });
      } catch (e) {
        console.error('Subdomain check error:', e);
        setAvailability({ state: 'unknown', reason: 'Unable to check availability. Try again.' });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [subdomain, editing, firm, firmId, accessToken]);

  const cancel = () => {
    setEditing(false);
    setName(firm?.name || '');
    setSubdomain(firm?.subdomain || '');
    setError('');
    setAvailability({ state: 'idle' });
  };

  const save = async () => {
    if (!firm) return;
    setError('');
    if (!canSaveProfile(name, subdomain, firm.subdomain, availability.state)) {
      setError(!name.trim() ? 'Firm name is required' : !subdomain.trim() ? 'Subdomain is required' : 'Choose an available subdomain');
      return;
    }
    const body: Record<string, string> = {};
    if (name.trim() !== firm.name) body.name = name.trim();
    if (subdomain !== firm.subdomain) body.subdomain = subdomain;
    if (Object.keys(body).length === 0) { setEditing(false); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/firms/${firmId}`, { method: 'PUT', headers: getAuthHeaders(accessToken), body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not save the firm (${res.status})`);
      setFirm(data.firm);
      setName(data.firm?.name || '');
      setSubdomain(data.firm?.subdomain || '');
      setEditing(false);
      if (data.subdomainChanged) {
        const url = getTenantUrl(data.firm.subdomain);
        setRedirectingTo(url);
        toast.success('Firm saved. Taking you to the new address…');
        setTimeout(() => { window.location.href = url; }, 1500);
      } else {
        toast.success('Firm saved');
      }
    } catch (e: any) {
      setError(e?.message || 'Could not save the firm');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Card><CardContent className="py-12 flex justify-center"><Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading" /></CardContent></Card>;
  }
  if (!firm) {
    return (
      <Card>
        <CardHeader><CardTitle>Firm profile</CardTitle></CardHeader>
        <CardContent><Alert variant="destructive"><AlertDescription>{error || 'Firm information is not available.'}</AlertDescription></Alert></CardContent>
      </Card>
    );
  }

  const subdomainChanged = editing && subdomain !== firm.subdomain;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Building2 className="size-5 text-primary" aria-hidden="true" />Firm profile</CardTitle>
        <CardDescription>The firm's name and the address your team and clients sign in at.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {redirectingTo && (
          <Alert className="border-green-500 bg-green-50">
            <CheckCircle className="size-4 text-green-600" aria-hidden="true" />
            <AlertDescription className="text-green-800">Firm saved. Redirecting to <code>{redirectingTo}</code>…</AlertDescription>
          </Alert>
        )}
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

        <div className="border rounded-lg p-4 bg-muted/30 space-y-4">
          {editing ? (
            <div className="space-y-2">
              <Label htmlFor="firm-name">Firm name</Label>
              <Input id="firm-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your firm's name" maxLength={120} />
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-lg">{firm.name}</div>
                <div className="text-muted-foreground">Firm ID: {firm.id}</div>
              </div>
              <Badge variant="secondary">{firm.subscriptionTier ? firm.subscriptionTier : 'Active'}</Badge>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="firm-subdomain">Subdomain {!editing && <span className="text-muted-foreground">(your sign-in address)</span>}</Label>
            {editing ? (
              <>
                <div className="flex items-center gap-2">
                  <Input id="firm-subdomain" value={subdomain} onChange={(e) => setSubdomain(cleanSubdomainInput(e.target.value))} placeholder="yourfirm" autoComplete="off" />
                  <span className="text-muted-foreground whitespace-nowrap">.pathkit.pro</span>
                </div>
                {subdomainChanged && (
                  <div className="flex items-center gap-2" aria-live="polite">
                    {availability.state === 'checking' && (<><Loader2 className="size-4 animate-spin" aria-hidden="true" /><span className="text-muted-foreground">Checking availability…</span></>)}
                    {availability.state === 'available' && (
                      <Badge variant="outline" className="border-green-500 text-green-700 bg-green-50"><CheckCircle className="size-3 mr-1" aria-hidden="true" />Available</Badge>
                    )}
                    {availability.state === 'taken' && (
                      <><Badge variant="outline" className="border-red-500 text-red-700 bg-red-50"><XCircle className="size-3 mr-1" aria-hidden="true" />Not available</Badge><span className="text-muted-foreground">{availability.reason}</span></>
                    )}
                    {availability.state === 'unknown' && <span className="text-muted-foreground">{availability.reason}</span>}
                  </div>
                )}
                <p className="text-muted-foreground">Lowercase letters, numbers and hyphens; at least 3 characters.</p>
              </>
            ) : (
              <code className="inline-block px-2 py-1 bg-muted rounded">https://{firm.subdomain}.pathkit.pro</code>
            )}
          </div>

          {!editing && stats && (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 border-t pt-3 sm:grid-cols-4" aria-label="Firm stats">
              {STAT_LABELS.map((s) => (
                <div key={s.key}>
                  <dt className="text-muted-foreground">{s.label}</dt>
                  <dd>{stats[s.key]}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {subdomainChanged && (
          <Alert>
            <AlertTriangle className="size-4" aria-hidden="true" />
            <AlertDescription>Changing the subdomain moves everyone's sign-in address. Your data stays as it is; tell your team and clients to update their bookmarks and any saved links.</AlertDescription>
          </Alert>
        )}

        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button onClick={save} disabled={saving || !canSaveProfile(name, subdomain, firm.subdomain, availability.state)} className="gap-2">
                {saving ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Save className="size-4" aria-hidden="true" />}
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
              <Button variant="outline" onClick={cancel} disabled={saving}><X className="size-4 mr-2" aria-hidden="true" />Cancel</Button>
            </>
          ) : (
            <Button onClick={() => setEditing(true)} disabled={!!redirectingTo}>Edit firm profile</Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
